import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { 
  ArrowLeft, Users, FileText, StickyNote, Image, FolderOpen, Plus, Trash2, 
  Phone, Mail, MapPin, Building, Download, Eye, Calendar, MessageSquare,
  Upload, X, Edit, Save, Clock, CheckCircle2, CheckCircle, Send, Printer, UserPlus, Edit2,
  DollarSign, Receipt, FileDown, ClipboardList, Globe, Pencil, Check
} from 'lucide-react';
import CompanyLogo from '../components/CompanyLogo';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../i18n/LanguageContext';
import { COMPANY } from '../config/company';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import ClientAccountingTab from '../components/client/tabs/ClientAccountingTab';
import ClientOverviewTab from '../components/client/tabs/ClientOverviewTab';
import ClientProjectTab from '../components/client/tabs/ClientProjectTab';
import ClientPhotosTab from '../components/client/tabs/ClientPhotosTab';
import ClientProposalsTab from '../components/client/tabs/ClientProposalsTab';
import ClientContactsTab, { CONTACT_ROLES } from '../components/client/tabs/ClientContactsTab';
import ClientNotesTab from '../components/client/tabs/ClientNotesTab';
import ClientDocumentsTab from '../components/client/tabs/ClientDocumentsTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Contact roles for client-specific contacts
// CONTACT_ROLES moved to ClientContactsTab.jsx

const ClientDetail = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [settings, setSettings] = useState({});
  
  // Notes
  const [newNote, setNewNote] = useState({ content: '', note_type: 'general' });
  
  // Project Notes (for construction flow chart)
  const [newProjectNote, setNewProjectNote] = useState('');
  
  // Contacts
  const [contacts, setContacts] = useState([]);
  const [showContactModal, setShowContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({
    name: '', role: 'homeowner', company: '', email: '', phone: '', mobile: '', notes: ''
  });
  
  // File uploads
  const docInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const printRef = useRef(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  
  // Upload modals
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [fileTitle, setFileTitle] = useState('');
  const [fileDescription, setFileDescription] = useState('');

  // Estimates linked to this client
  const [clientEstimates, setClientEstimates] = useState([]);

  // Accounting state
  const [accountingSummary, setAccountingSummary] = useState(null);
  const [accountingPayments, setAccountingPayments] = useState([]);
  const [accountingCosts, setAccountingCosts] = useState([]);
  const [accountingInvoices, setAccountingInvoices] = useState([]);
  const [accountingActivities, setAccountingActivities] = useState([]);
  const [costCategories, setCostCategories] = useState([]);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showAddCost, setShowAddCost] = useState(false);
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [newPayment, setNewPayment] = useState({ amount: '', payment_date: '', payment_method: 'check', reference_number: '', notes: '', project_name: '' });
  const [newCost, setNewCost] = useState({ category: 'materials', description: '', amount: '', date: '', vendor: '', vendor_contact_id: '', receipt_number: '', notes: '', is_billable: false, project_name: '' });
  const [newInvoice, setNewInvoice] = useState({ description: 'Services Rendered', amount: '', project_name: '' });
  
  // Edit states for costs and invoices
  const [editingCost, setEditingCost] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editingContractAmount, setEditingContractAmount] = useState(false);
  const [contractAmountOverride, setContractAmountOverride] = useState('');
  
  // Vendor search and create states
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendorSearchResults, setVendorSearchResults] = useState([]);
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [showCreateVendor, setShowCreateVendor] = useState(false);
  const [newVendorForm, setNewVendorForm] = useState({ name: '', company: '', email: '', phone: '', role: 'supplier' });
  const [allVendorContacts, setAllVendorContacts] = useState([]);

  // Change Orders state
  const [changeOrders, setChangeOrders] = useState([]);
  const [showCreateChangeOrder, setShowCreateChangeOrder] = useState(false);
  const [editingChangeOrder, setEditingChangeOrder] = useState(null);
  const [newChangeOrder, setNewChangeOrder] = useState({
    description: '',
    reason: '',
    project_name: '',
    line_items: [{ id: Date.now(), description: '', quantity: 1, unit_price: 0 }],
    status: 'draft',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchClient();
    fetchContacts();
    fetchCostCategories();
    fetchSettings();
    fetchAllVendorContacts();
    fetchChangeOrders();
  }, [clientId]);

  // Fetch accounting data when switching to accounting tab
  useEffect(() => {
    if (activeTab === 'accounting' && clientId) {
      fetchAccountingData();
    }
  // eslint-disable-next-line
  }, [activeTab, clientId]);

  // Company info from settings
  const companyInfo = {
    name: settings.name || COMPANY.name,
    license: settings.license || COMPANY.license,
    license2: settings.license2 || '',
    license3: settings.license3 || '',
    license4: settings.license4 || '',
    license5: settings.license5 || '',
    address: settings.address || COMPANY.address,
    city: settings.city || '',
    state: settings.state || '',
    zip: settings.zip || '',
    phone: settings.phone || COMPANY.phone,
    email: settings.email || COMPANY.email,
    website: settings.website || COMPANY.website,
    logo: settings.logo || COMPANY.logo,
    primaryColor: settings.primaryColor || COMPANY.primaryColor,
  };

  // Get all licenses as a formatted string
  const getAllLicenses = () => {
    const licenses = [companyInfo.license, companyInfo.license2, companyInfo.license3, companyInfo.license4, companyInfo.license5]
      .filter(l => l && l.trim());
    return licenses.join(' | ');
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  // Fetch change orders for this client
  const fetchChangeOrders = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/accounting/client/${clientId}/change-orders`);
      if (response.ok) {
        const data = await response.json();
        setChangeOrders(data);
      }
    } catch (error) {
      console.error('Error fetching change orders:', error);
    }
  };

  const fetchClient = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/${clientId}`);
      if (response.ok) {
        const data = await response.json();
        setClient(data);
        setEditData(data);
        // Fetch estimates that match this client
        fetchClientEstimates(data);
      } else {
        toast.error('Client not found');
        navigate('/clients');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/contacts/client/${clientId}`);
      if (response.ok) {
        const data = await response.json();
        setContacts(data);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  // Fetch all vendor contacts (subcontractors and suppliers) globally
  const fetchAllVendorContacts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/contacts`);
      if (response.ok) {
        const data = await response.json();
        // Filter to vendor types: subcontractor, supplier, or any contact with company name
        const vendors = data.filter(c => 
          c.role === 'subcontractor' || 
          c.role === 'supplier' || 
          c.company
        );
        setAllVendorContacts(vendors);
      }
    } catch (error) {
      console.error('Error fetching vendor contacts:', error);
    }
  };

  // Search vendors as user types
  const handleVendorSearch = (searchText) => {
    setVendorSearch(searchText);
    setNewCost(prev => ({ ...prev, vendor: searchText, vendor_contact_id: '' }));
    
    if (searchText.length > 0) {
      const results = allVendorContacts.filter(v => 
        (v.name && v.name.toLowerCase().includes(searchText.toLowerCase())) ||
        (v.company && v.company.toLowerCase().includes(searchText.toLowerCase()))
      );
      setVendorSearchResults(results);
      setShowVendorDropdown(true);
    } else {
      setVendorSearchResults([]);
      setShowVendorDropdown(false);
    }
  };

  // Handle client flag/field changes with auto-save
  const handleInputChange = async (field, value) => {
    try {
      const updatedClient = { ...client, [field]: value };
      setClient(updatedClient);
      
      await fetch(`${BACKEND_URL}/api/clients/${clientId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value })
      });
    } catch (error) {
      console.error('Error updating client:', error);
      toast.error(language === 'es' ? 'Error al guardar' : 'Error saving');
    }
  };

  // Select existing vendor
  const selectVendor = (vendor) => {
    const displayName = vendor.company || vendor.name;
    setVendorSearch(displayName);
    setNewCost(prev => ({ ...prev, vendor: displayName, vendor_contact_id: vendor.id }));
    setShowVendorDropdown(false);
  };

  // Create new vendor contact
  const createVendorContact = async () => {
    if (!newVendorForm.name.trim() && !newVendorForm.company.trim()) {
      toast.error(language === 'es' ? 'Nombre o empresa requerido' : 'Name or company required');
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newVendorForm,
          contact_type: 'vendor',
          client_id: clientId // Link to current client as well
        })
      });
      
      if (response.ok) {
        const createdContact = await response.json();
        toast.success(language === 'es' ? '¡Contacto de proveedor creado!' : 'Vendor contact created!');
        
        // Select the newly created vendor
        const displayName = createdContact.company || createdContact.name;
        setVendorSearch(displayName);
        setNewCost(prev => ({ ...prev, vendor: displayName, vendor_contact_id: createdContact.id }));
        
        // Reset and close
        setShowCreateVendor(false);
        setNewVendorForm({ name: '', company: '', email: '', phone: '', role: 'supplier' });
        
        // Refresh vendor list
        fetchAllVendorContacts();
        fetchContacts();
      }
    } catch (error) {
      console.error('Error creating vendor:', error);
      toast.error(language === 'es' ? 'Error al crear contacto' : 'Error creating contact');
    }
  };

  // Contact CRUD
  const saveContact = async () => {
    if (!contactForm.name.trim()) {
      toast.error(language === 'es' ? 'Nombre requerido' : 'Name is required');
      return;
    }
    
    try {
      const url = editingContact 
        ? `${BACKEND_URL}/api/contacts/${editingContact.id}`
        : `${BACKEND_URL}/api/contacts`;
      
      const contactData = {
        ...contactForm,
        contact_type: 'client',
        client_id: clientId
      };
      
      const response = await fetch(url, {
        method: editingContact ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactData)
      });
      
      if (response.ok) {
        toast.success(editingContact 
          ? (language === 'es' ? 'Contacto actualizado' : 'Contact updated')
          : (language === 'es' ? 'Contacto agregado' : 'Contact added')
        );
        fetchContacts();
        closeContactModal();
      }
    } catch (error) {
      console.error('Error saving contact:', error);
      toast.error(language === 'es' ? 'Error al guardar' : 'Failed to save');
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar este contacto?' : 'Delete this contact?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/contacts/${contactId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success(language === 'es' ? 'Contacto eliminado' : 'Contact deleted');
        fetchContacts();
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
    }
  };

  const openNewContactModal = () => {
    setEditingContact(null);
    setContactForm({ name: '', role: 'homeowner', company: '', email: '', phone: '', mobile: '', notes: '' });
    setShowContactModal(true);
  };

  const openEditContactModal = (contact) => {
    setEditingContact(contact);
    setContactForm({
      name: contact.name || '',
      role: contact.role || 'homeowner',
      company: contact.company || '',
      email: contact.email || '',
      phone: contact.phone || '',
      mobile: contact.mobile || '',
      notes: contact.notes || ''
    });
    setShowContactModal(true);
  };

  const closeContactModal = () => {
    setShowContactModal(false);
    setEditingContact(null);
  };

  // ===== CHANGE ORDER FUNCTIONS =====
  const handleCreateChangeOrder = async () => {
    if (!newChangeOrder.description.trim()) {
      toast.error(language === 'es' ? 'La descripción es requerida' : 'Description is required');
      return;
    }
    
    // Filter out empty line items
    const validLineItems = newChangeOrder.line_items.filter(item => 
      item.description.trim() && (item.quantity > 0 || item.unit_price > 0)
    );
    
    if (validLineItems.length === 0) {
      toast.error(language === 'es' ? 'Agregue al menos una partida' : 'Add at least one line item');
      return;
    }
    
    // Retry logic for intermittent 520 errors
    const maxRetries = 3;
    let lastError = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(`${BACKEND_URL}/api/accounting/change-orders`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            client_id: clientId,
            ...newChangeOrder,
            line_items: validLineItems
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          toast.success(language === 'es' ? '¡Orden de cambio creada!' : 'Change order created!');
          setShowCreateChangeOrder(false);
          setNewChangeOrder({
            description: '',
            reason: '',
            project_name: '',
            line_items: [{ id: Date.now(), description: '', quantity: 1, unit_price: 0 }],
            status: 'draft',
            date: new Date().toISOString().split('T')[0]
          });
          fetchChangeOrders();
          fetchAccountingData();
          return; // Success - exit the function
        } else if (response.status === 520 || response.status === 522 || response.status === 524) {
          // CloudFlare timeout errors - retry
          lastError = `Server timeout (${response.status})`;
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt)); // Wait 1s, 2s, 3s
            continue;
          }
        } else {
          const errorText = await response.text();
          console.error('Server error:', response.status, errorText);
          toast.error(language === 'es' ? `Error del servidor: ${response.status}` : `Server error: ${response.status}`);
          return;
        }
      } catch (error) {
        console.error('Error:', error);
        lastError = error.message;
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
          continue;
        }
      }
    }
    
    // All retries failed
    toast.error(language === 'es' ? `Error de conexión después de ${maxRetries} intentos. Por favor intente de nuevo.` : `Connection error after ${maxRetries} attempts. Please try again.`);
  };

  const handleUpdateChangeOrder = async (changeOrderId, updates) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/accounting/change-orders/${changeOrderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      if (response.ok) {
        toast.success(language === 'es' ? '¡Orden de cambio actualizada!' : 'Change order updated!');
        setEditingChangeOrder(null);
        fetchChangeOrders();
        fetchAccountingData();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error al actualizar' : 'Error updating');
    }
  };

  const handleDeleteChangeOrder = async (changeOrderId) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar esta orden de cambio?' : 'Delete this change order?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/accounting/change-orders/${changeOrderId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success(language === 'es' ? 'Orden de cambio eliminada' : 'Change order deleted');
        fetchChangeOrders();
        fetchAccountingData();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const addChangeOrderLineItem = () => {
    setNewChangeOrder(prev => ({
      ...prev,
      line_items: [...prev.line_items, { id: Date.now(), description: '', quantity: 1, unit_price: 0 }]
    }));
  };

  const updateChangeOrderLineItem = (itemId, field, value) => {
    setNewChangeOrder(prev => ({
      ...prev,
      line_items: prev.line_items.map(item => 
        item.id === itemId ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeChangeOrderLineItem = (itemId) => {
    setNewChangeOrder(prev => ({
      ...prev,
      line_items: prev.line_items.filter(item => item.id !== itemId)
    }));
  };

  const calculateChangeOrderTotal = (items) => {
    return items.reduce((sum, item) => sum + ((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)), 0);
  };

  // Edit Change Order functions
  const handleEditChangeOrderSave = async () => {
    if (!editingChangeOrder) return;
    
    // Filter out empty line items
    const validLineItems = editingChangeOrder.line_items.filter(item => 
      item.description && item.description.trim() && (item.quantity > 0 || item.unit_price > 0)
    );
    
    if (validLineItems.length === 0) {
      toast.error(language === 'es' ? 'Agregue al menos una partida' : 'Add at least one line item');
      return;
    }
    
    await handleUpdateChangeOrder(editingChangeOrder.id, {
      description: editingChangeOrder.description,
      reason: editingChangeOrder.reason,
      project_name: editingChangeOrder.project_name,
      line_items: validLineItems,
      status: editingChangeOrder.status
    });
  };

  const addEditingChangeOrderLineItem = () => {
    setEditingChangeOrder(prev => ({
      ...prev,
      line_items: [...(prev.line_items || []), { id: Date.now(), description: '', quantity: 1, unit_price: 0 }]
    }));
  };

  const updateEditingChangeOrderLineItem = (itemId, field, value) => {
    setEditingChangeOrder(prev => ({
      ...prev,
      line_items: prev.line_items.map(item => 
        item.id === itemId ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeEditingChangeOrderLineItem = (itemId) => {
    setEditingChangeOrder(prev => ({
      ...prev,
      line_items: prev.line_items.filter(item => item.id !== itemId)
    }));
  };

  const fetchClientEstimates = async (clientData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/estimates`);
      if (response.ok) {
        const allEstimates = await response.json();
        // Filter estimates that match by email, name, or address
        const matchingEstimates = allEstimates.filter(est => {
          const emailMatch = clientData.email && est.client_email && 
            clientData.email.toLowerCase() === est.client_email.toLowerCase();
          const nameMatch = clientData.name && est.client_name && 
            clientData.name.toLowerCase() === est.client_name.toLowerCase();
          const addressMatch = clientData.address && est.client_address && 
            clientData.address.toLowerCase().includes(est.client_address.toLowerCase().substring(0, 20));
          return emailMatch || nameMatch || addressMatch;
        });
        setClientEstimates(matchingEstimates);
      }
    } catch (error) {
      console.error('Error fetching estimates:', error);
    }
  };

  // Create a new proposal for this client
  const createNewProposal = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/estimates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_name: client?.name || '',
          client_email: client?.email || '',
          client_phone: client?.phone || '',
          client_address: client?.address || '',
          client_city: client?.city || '',
          client_state: client?.state || 'FL',
          client_zip: client?.zip_code || '',
          project_type: 'kitchen',
          status: 'draft',
          lead_status: 'estimate_provided',
          general_fees: [],
          line_items: [],
          subtotal: 0,
          tax_rate: 7.0,
          tax_amount: 0,
          grand_total: 0,
          terms_and_conditions: '',
          estimate_date: new Date().toISOString().split('T')[0]
        })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(language === 'es' ? '¡Propuesta creada!' : 'Proposal created!');
        navigate(`/leads/${data.id}`);
      } else {
        toast.error(language === 'es' ? 'Error al crear propuesta' : 'Error creating proposal');
      }
    } catch (error) {
      console.error('Error creating proposal:', error);
      toast.error(language === 'es' ? 'Error al crear propuesta' : 'Error creating proposal');
    }
  };

  // ===== ACCOUNTING FUNCTIONS =====
  const fetchCostCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/accounting/cost-categories`);
      if (response.ok) {
        const data = await response.json();
        setCostCategories(data);
      }
    } catch (error) {
      console.error('Error fetching cost categories:', error);
    }
  };

  const fetchAccountingData = async () => {
    try {
      const [summaryRes, paymentsRes, costsRes, invoicesRes, activitiesRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/accounting/client/${clientId}/summary`),
        fetch(`${BACKEND_URL}/api/accounting/client/${clientId}/payments`),
        fetch(`${BACKEND_URL}/api/accounting/client/${clientId}/costs`),
        fetch(`${BACKEND_URL}/api/accounting/client/${clientId}/invoices`),
        fetch(`${BACKEND_URL}/api/accounting/client/${clientId}/activities`)
      ]);
      
      if (summaryRes.ok) setAccountingSummary(await summaryRes.json());
      if (paymentsRes.ok) setAccountingPayments(await paymentsRes.json());
      if (costsRes.ok) setAccountingCosts(await costsRes.json());
      if (invoicesRes.ok) setAccountingInvoices(await invoicesRes.json());
      if (activitiesRes.ok) setAccountingActivities(await activitiesRes.json());
    } catch (error) {
      console.error('Error fetching accounting data:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const handleAddPayment = async () => {
    if (!newPayment.amount || !newPayment.payment_date) {
      toast.error(language === 'es' ? 'Monto y fecha son requeridos' : 'Amount and date are required');
      return;
    }
    try {
      const response = await fetch(`${BACKEND_URL}/api/accounting/payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newPayment, amount: parseFloat(newPayment.amount), client_id: clientId })
      });
      if (response.ok) {
        toast.success(language === 'es' ? '¡Pago registrado!' : 'Payment recorded!');
        setShowAddPayment(false);
        setNewPayment({ amount: '', payment_date: '', payment_method: 'check', reference_number: '', notes: '', project_name: '' });
        fetchAccountingData();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error al guardar pago' : 'Error saving payment');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar este pago?' : 'Delete this payment?')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/accounting/payments/${paymentId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success(language === 'es' ? 'Pago eliminado' : 'Payment deleted');
        fetchAccountingData();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleAddCost = async () => {
    if (!newCost.amount || !newCost.date || !newCost.description) {
      toast.error(language === 'es' ? 'Descripción, monto y fecha son requeridos' : 'Description, amount and date are required');
      return;
    }
    try {
      const response = await fetch(`${BACKEND_URL}/api/accounting/costs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          ...newCost, 
          amount: parseFloat(newCost.amount), 
          client_id: clientId,
          vendor_contact_id: newCost.vendor_contact_id || null
        })
      });
      if (response.ok) {
        toast.success(language === 'es' ? '¡Costo registrado!' : 'Cost recorded!');
        setShowAddCost(false);
        setNewCost({ category: 'materials', description: '', amount: '', date: '', vendor: '', vendor_contact_id: '', receipt_number: '', notes: '', is_billable: false, project_name: '' });
        setVendorSearch('');
        fetchAccountingData();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error al guardar costo' : 'Error saving cost');
    }
  };

  const handleDeleteCost = async (costId) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar este costo?' : 'Delete this cost?')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/accounting/costs/${costId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success(language === 'es' ? 'Costo eliminado' : 'Cost deleted');
        fetchAccountingData();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleCreateInvoice = async () => {
    try {
      const invoiceAmount = newInvoice.amount ? parseFloat(newInvoice.amount) : (accountingSummary?.contract_total || 0);
      const response = await fetch(`${BACKEND_URL}/api/accounting/invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          client_id: clientId,
          description: newInvoice.description || 'Services Rendered',
          amount: invoiceAmount,
          project_name: newInvoice.project_name
        })
      });
      if (response.ok) {
        toast.success(language === 'es' ? '¡Factura creada!' : 'Invoice created!');
        setShowCreateInvoice(false);
        setNewInvoice({ description: 'Services Rendered', amount: '', project_name: '' });
        fetchAccountingData();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error al crear factura' : 'Error creating invoice');
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/accounting/invoices/${invoiceId}/send`, { method: 'POST' });
      const data = await response.json();
      if (response.ok) {
        toast.success(language === 'es' ? '¡Factura enviada!' : 'Invoice sent!');
        fetchAccountingData();
      } else {
        toast.error(data.detail || (language === 'es' ? 'Error al enviar factura' : 'Error sending invoice'));
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error al enviar factura' : 'Error sending invoice');
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar esta factura?' : 'Delete this invoice?')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/accounting/invoices/${invoiceId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success(language === 'es' ? 'Factura eliminada' : 'Invoice deleted');
        fetchAccountingData();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Edit Cost Handler
  const handleEditCost = async () => {
    if (!editingCost) return;
    if (!editingCost.amount || !editingCost.date || !editingCost.description) {
      toast.error(language === 'es' ? 'Descripción, monto y fecha son requeridos' : 'Description, amount and date are required');
      return;
    }
    try {
      const response = await fetch(`${BACKEND_URL}/api/accounting/costs/${editingCost.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...editingCost,
          amount: parseFloat(editingCost.amount),
          client_id: clientId
        })
      });
      if (response.ok) {
        toast.success(language === 'es' ? '¡Costo actualizado!' : 'Cost updated!');
        setEditingCost(null);
        fetchAccountingData();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error al actualizar costo' : 'Error updating cost');
    }
  };

  // Edit Invoice Handler
  const handleEditInvoice = async () => {
    if (!editingInvoice) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/accounting/invoices/${editingInvoice.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...editingInvoice,
          amount: parseFloat(editingInvoice.amount),
          client_id: clientId
        })
      });
      if (response.ok) {
        toast.success(language === 'es' ? '¡Factura actualizada!' : 'Invoice updated!');
        setEditingInvoice(null);
        fetchAccountingData();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error al actualizar factura' : 'Error updating invoice');
    }
  };

  const handleExportReport = async (format = 'excel') => {
    try {
      window.open(`${BACKEND_URL}/api/accounting/client/${clientId}/report?format=${format}`, '_blank');
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error al exportar' : 'Error exporting');
    }
  };

  // Save contract amount override
  const handleSaveContractAmount = async () => {
    try {
      const amount = parseFloat(contractAmountOverride);
      if (isNaN(amount) || amount < 0) {
        toast.error(language === 'es' ? 'Monto inválido' : 'Invalid amount');
        return;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/clients/${clientId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...client,
          contract_amount_override: amount
        })
      });
      
      if (response.ok) {
        toast.success(language === 'es' ? '¡Monto actualizado!' : 'Amount updated!');
        setEditingContractAmount(false);
        fetchAccountingData();
        fetchClient();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error al guardar' : 'Error saving');
    }
  };

  // Invoice PDF generation with Save As dialog
  const generateInvoicePDF = async (invoice) => {
    const invoiceHtml = createInvoiceHTML(invoice);
    const container = document.createElement('div');
    container.innerHTML = invoiceHtml;
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.width = '800px';
    document.body.appendChild(container);

    try {
      const canvas = await html2canvas(container, { scale: 2, useCORS: true, logging: false });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'letter');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      
      const filename = `Invoice_${invoice.invoice_number}.pdf`;
      const pdfBlob = pdf.output('blob');
      
      // Try Save As dialog (modern browsers)
      if (window.showSaveFilePicker) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: 'PDF Document',
              accept: { 'application/pdf': ['.pdf'] }
            }]
          });
          const writable = await handle.createWritable();
          await writable.write(pdfBlob);
          await writable.close();
          toast.success(language === 'es' ? '¡PDF guardado!' : 'PDF saved!');
        } catch (err) {
          if (err.name !== 'AbortError') {
            // Fallback if Save As fails
            pdf.save(filename);
            toast.success(language === 'es' ? `¡PDF descargado: ${filename}!` : `PDF downloaded: ${filename}!`);
          }
        }
      } else {
        // Fallback for older browsers - open in new tab
        const blobUrl = URL.createObjectURL(pdfBlob);
        window.open(blobUrl, '_blank');
        toast.success(language === 'es' ? 'PDF abierto en nueva pestaña - use Ctrl+S para guardar' : 'PDF opened in new tab - use Ctrl+S to save');
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(language === 'es' ? 'Error al generar PDF' : 'Error generating PDF');
    } finally {
      document.body.removeChild(container);
    }
  };

  const createInvoiceHTML = (invoice) => {
    const allLicenses = getAllLicenses();
    const fullCompanyAddress = `${companyInfo.address || ''}, ${companyInfo.city || ''}, ${companyInfo.state || ''} ${companyInfo.zip || ''}`.replace(/^, |, $/g, '').trim();
    const fullClientAddress = client?.address || invoice.client_address || '';
    
    return `
      <div style="padding: 40px; max-width: 800px; margin: 0 auto; font-family: Arial, sans-serif; background: white;">
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
          <tr>
            <td style="vertical-align: top; width: 60%;">
              ${companyInfo.logo ? `<img src="${companyInfo.logo}" alt="Logo" style="max-height: 60px; margin-bottom: 10px; display: block;" />` : ''}
              <h1 style="color: ${companyInfo.primaryColor}; margin: 0; font-size: 24px;">${companyInfo.name}</h1>
              ${allLicenses ? `<p style="margin: 3px 0; color: #666; font-size: 11px;">${allLicenses}</p>` : ''}
              ${fullCompanyAddress ? `<p style="margin: 3px 0; color: #666; font-size: 12px;">${fullCompanyAddress}</p>` : ''}
              <p style="margin: 3px 0; color: #666; font-size: 12px;">${companyInfo.phone || ''} ${companyInfo.phone && companyInfo.email ? '|' : ''} ${companyInfo.email || ''}</p>
              ${companyInfo.website ? `<p style="margin: 3px 0; color: #666; font-size: 12px;">${companyInfo.website}</p>` : ''}
            </td>
            <td style="vertical-align: top; text-align: right; width: 40%;">
              <h2 style="color: ${companyInfo.primaryColor}; margin: 0; font-size: 28px; font-weight: bold;">INVOICE</h2>
              <p style="margin: 5px 0; font-weight: bold; font-size: 16px;">#${invoice.invoice_number}</p>
              <p style="margin: 3px 0; color: #666; font-size: 12px;">Date: ${invoice.invoice_date}</p>
              <p style="margin: 3px 0; color: #666; font-size: 12px;">Due: ${invoice.due_date}</p>
            </td>
          </tr>
        </table>
        <div style="border-bottom: 3px solid ${companyInfo.primaryColor}; margin-bottom: 25px;"></div>
        <div style="background: #f5f5f5; padding: 15px; border-radius: 6px; margin-bottom: 25px;">
          <h3 style="margin: 0 0 8px 0; color: ${companyInfo.primaryColor}; font-size: 14px;">BILL TO:</h3>
          <p style="margin: 3px 0; font-weight: bold; font-size: 16px;">${client?.name || invoice.client_name || ''}</p>
          ${fullClientAddress ? `<p style="margin: 3px 0; font-size: 13px;">${fullClientAddress}</p>` : ''}
          ${client?.phone || invoice.client_phone ? `<p style="margin: 3px 0; font-size: 13px;">${client?.phone || invoice.client_phone}</p>` : ''}
          ${client?.email || invoice.client_email ? `<p style="margin: 3px 0; font-size: 13px;">${client?.email || invoice.client_email}</p>` : ''}
        </div>
        <div style="margin-bottom: 25px;">
          <h3 style="color: ${companyInfo.primaryColor}; border-bottom: 1px solid #ddd; padding-bottom: 8px; font-size: 14px;">DESCRIPTION</h3>
          <p style="font-size: 14px; margin: 10px 0;">${invoice.description}</p>
        </div>
        <table style="width: 280px; margin-left: auto; border: 2px solid ${companyInfo.primaryColor}; border-radius: 6px; border-collapse: collapse;">
          <tr style="background: ${companyInfo.primaryColor}; color: white;">
            <td style="padding: 12px; font-size: 15px; font-weight: bold;">TOTAL DUE:</td>
            <td style="padding: 12px; text-align: right; font-size: 15px; font-weight: bold;">${formatCurrency(invoice.amount)}</td>
          </tr>
        </table>
        <div style="margin-top: 30px; padding: 15px; background: #f0f7ff; border-left: 4px solid ${companyInfo.primaryColor}; border-radius: 4px;">
          <h4 style="margin: 0 0 8px 0; color: ${companyInfo.primaryColor}; font-size: 13px;">PAYMENT TERMS</h4>
          <p style="margin: 0; color: #333; font-size: 12px;">${invoice.acceptance_terms || 'Payment due within 7 business days.'}</p>
        </div>
        <div style="margin-top: 30px; text-align: center; color: #999; font-size: 11px; border-top: 1px solid #ddd; padding-top: 15px;">
          <p style="margin: 5px 0;">Thank you for your business!</p>
          <p style="margin: 5px 0;">${companyInfo.name} | ${companyInfo.phone} | ${companyInfo.email}</p>
        </div>
      </div>
    `;
  };

  // Change Order PDF generation
  // Change Order PDF generation with Save As dialog
  const generateChangeOrderPDF = async (changeOrder) => {
    const coHtml = createChangeOrderHTML(changeOrder);
    const container = document.createElement('div');
    container.innerHTML = coHtml;
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.width = '800px';
    document.body.appendChild(container);

    try {
      const canvas = await html2canvas(container, { scale: 2, useCORS: true, logging: false });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'letter');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      
      const filename = `ChangeOrder_${changeOrder.change_order_number}.pdf`;
      const pdfBlob = pdf.output('blob');
      
      // Try Save As dialog (modern browsers)
      if (window.showSaveFilePicker) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: 'PDF Document',
              accept: { 'application/pdf': ['.pdf'] }
            }]
          });
          const writable = await handle.createWritable();
          await writable.write(pdfBlob);
          await writable.close();
          toast.success(language === 'es' ? '¡PDF guardado!' : 'PDF saved!');
        } catch (err) {
          if (err.name !== 'AbortError') {
            // Fallback if Save As fails
            pdf.save(filename);
            toast.success(language === 'es' ? `¡PDF descargado: ${filename}!` : `PDF downloaded: ${filename}!`);
          }
        }
      } else {
        // Fallback for older browsers - open in new tab
        const blobUrl = URL.createObjectURL(pdfBlob);
        window.open(blobUrl, '_blank');
        toast.success(language === 'es' ? 'PDF abierto en nueva pestaña - use Ctrl+S para guardar' : 'PDF opened in new tab - use Ctrl+S to save');
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(language === 'es' ? 'Error al generar PDF' : 'Error generating PDF');
    } finally {
      document.body.removeChild(container);
    }
  };

  const createChangeOrderHTML = (changeOrder) => {
    const allLicenses = getAllLicenses();
    const fullCompanyAddress = `${companyInfo.address || ''}, ${companyInfo.city || ''}, ${companyInfo.state || ''} ${companyInfo.zip || ''}`.replace(/^, |, $/g, '').trim();
    const fullClientAddress = client?.address || '';
    
    // Build line items table rows
    const lineItemsRows = (changeOrder.line_items || []).map((item, idx) => `
      <tr style="border-bottom: 1px solid #eee;">
        <td style="padding: 10px; font-size: 13px;">${idx + 1}</td>
        <td style="padding: 10px; font-size: 13px;">${item.description}</td>
        <td style="padding: 10px; text-align: center; font-size: 13px;">${item.quantity || 1}</td>
        <td style="padding: 10px; text-align: right; font-size: 13px;">${formatCurrency(item.unit_price || 0)}</td>
        <td style="padding: 10px; text-align: right; font-size: 13px; font-weight: bold;">${formatCurrency((item.quantity || 1) * (item.unit_price || 0))}</td>
      </tr>
    `).join('');
    
    return `
      <div style="padding: 40px; max-width: 800px; margin: 0 auto; font-family: Arial, sans-serif; background: white;">
        <!-- Header -->
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
          <tr>
            <td style="vertical-align: top; width: 60%;">
              ${companyInfo.logo ? `<img src="${companyInfo.logo}" alt="Logo" style="max-height: 60px; margin-bottom: 10px; display: block;" />` : ''}
              <h1 style="color: ${companyInfo.primaryColor}; margin: 0; font-size: 24px;">${companyInfo.name}</h1>
              ${allLicenses ? `<p style="margin: 3px 0; color: #666; font-size: 11px;">${allLicenses}</p>` : ''}
              ${fullCompanyAddress ? `<p style="margin: 3px 0; color: #666; font-size: 12px;">${fullCompanyAddress}</p>` : ''}
              <p style="margin: 3px 0; color: #666; font-size: 12px;">${companyInfo.phone || ''} ${companyInfo.phone && companyInfo.email ? '|' : ''} ${companyInfo.email || ''}</p>
            </td>
            <td style="vertical-align: top; text-align: right; width: 40%;">
              <h2 style="color: #7c3aed; margin: 0; font-size: 24px; font-weight: bold;">CHANGE ORDER</h2>
              <p style="margin: 5px 0; font-weight: bold; font-size: 18px; color: #7c3aed;">#${changeOrder.change_order_number}</p>
              <p style="margin: 3px 0; color: #666; font-size: 12px;">Date: ${changeOrder.date}</p>
              ${changeOrder.project_name ? `<p style="margin: 3px 0; color: #666; font-size: 12px;">Project: ${changeOrder.project_name}</p>` : ''}
              <p style="margin: 8px 0; padding: 4px 12px; background: ${
                changeOrder.status === 'approved' ? '#dcfce7' : 
                changeOrder.status === 'declined' ? '#fee2e2' : 
                changeOrder.status === 'sent' ? '#dbeafe' : '#f3f4f6'
              }; color: ${
                changeOrder.status === 'approved' ? '#166534' : 
                changeOrder.status === 'declined' ? '#991b1b' : 
                changeOrder.status === 'sent' ? '#1e40af' : '#374151'
              }; border-radius: 4px; display: inline-block; font-size: 11px; font-weight: bold;">
                ${changeOrder.status === 'approved' ? 'APPROVED' : 
                  changeOrder.status === 'declined' ? 'DECLINED' : 
                  changeOrder.status === 'sent' ? 'PENDING APPROVAL' : 'DRAFT'}
              </p>
            </td>
          </tr>
        </table>
        
        <div style="border-bottom: 3px solid #7c3aed; margin-bottom: 25px;"></div>
        
        <!-- Client Info -->
        <div style="background: #f5f3ff; padding: 15px; border-radius: 6px; margin-bottom: 25px; border-left: 4px solid #7c3aed;">
          <h3 style="margin: 0 0 8px 0; color: #7c3aed; font-size: 14px;">CLIENT:</h3>
          <p style="margin: 3px 0; font-weight: bold; font-size: 16px;">${client?.name || ''}</p>
          ${fullClientAddress ? `<p style="margin: 3px 0; font-size: 13px;">${fullClientAddress}</p>` : ''}
          ${client?.phone ? `<p style="margin: 3px 0; font-size: 13px;">${client.phone}</p>` : ''}
          ${client?.email ? `<p style="margin: 3px 0; font-size: 13px;">${client.email}</p>` : ''}
        </div>
        
        <!-- Description & Reason -->
        <div style="margin-bottom: 25px;">
          <h3 style="color: #7c3aed; font-size: 14px; margin-bottom: 8px;">DESCRIPTION OF CHANGE:</h3>
          <p style="font-size: 14px; margin: 0; padding: 10px; background: #faf5ff; border-radius: 4px;">${changeOrder.description}</p>
        </div>
        
        ${changeOrder.reason ? `
        <div style="margin-bottom: 25px;">
          <h3 style="color: #7c3aed; font-size: 14px; margin-bottom: 8px;">REASON FOR CHANGE:</h3>
          <p style="font-size: 14px; margin: 0; padding: 10px; background: #f9fafb; border-radius: 4px; color: #4b5563;">${changeOrder.reason}</p>
        </div>
        ` : ''}
        
        <!-- Line Items Table -->
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
          <thead>
            <tr style="background: #7c3aed; color: white;">
              <th style="padding: 10px; text-align: left; font-size: 12px; width: 40px;">#</th>
              <th style="padding: 10px; text-align: left; font-size: 12px;">Description</th>
              <th style="padding: 10px; text-align: center; font-size: 12px; width: 60px;">Qty</th>
              <th style="padding: 10px; text-align: right; font-size: 12px; width: 100px;">Unit Price</th>
              <th style="padding: 10px; text-align: right; font-size: 12px; width: 100px;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${lineItemsRows}
          </tbody>
        </table>
        
        <!-- Total -->
        <table style="width: 280px; margin-left: auto; border: 2px solid #7c3aed; border-radius: 6px; border-collapse: collapse;">
          <tr style="background: #7c3aed; color: white;">
            <td style="padding: 15px; font-size: 16px; font-weight: bold;">CHANGE ORDER TOTAL:</td>
            <td style="padding: 15px; text-align: right; font-size: 18px; font-weight: bold;">${formatCurrency(changeOrder.total_amount)}</td>
          </tr>
        </table>
        
        <!-- Signature Section -->
        <div style="margin-top: 40px; display: flex; justify-content: space-between;">
          <div style="width: 45%; border-top: 1px solid #333; padding-top: 10px;">
            <p style="margin: 0; font-size: 12px; color: #666;">Client Signature</p>
            <p style="margin: 5px 0 0 0; font-size: 11px; color: #999;">Date: _____________</p>
          </div>
          <div style="width: 45%; border-top: 1px solid #333; padding-top: 10px;">
            <p style="margin: 0; font-size: 12px; color: #666;">Contractor Signature</p>
            <p style="margin: 5px 0 0 0; font-size: 11px; color: #999;">Date: _____________</p>
          </div>
        </div>
        
        <!-- Terms -->
        <div style="margin-top: 30px; padding: 15px; background: #f5f3ff; border-left: 4px solid #7c3aed; border-radius: 4px;">
          <h4 style="margin: 0 0 8px 0; color: #7c3aed; font-size: 12px;">TERMS & CONDITIONS</h4>
          <p style="margin: 0; color: #4b5563; font-size: 11px;">This change order modifies the original contract. Work will not proceed until this change order is signed by both parties. Payment for change order work is due upon completion unless otherwise specified.</p>
        </div>
        
        <!-- Footer -->
        <div style="margin-top: 30px; text-align: center; color: #999; font-size: 11px; border-top: 1px solid #ddd; padding-top: 15px;">
          <p style="margin: 5px 0;">${companyInfo.name} | ${companyInfo.phone} | ${companyInfo.email}</p>
        </div>
      </div>
    `;
  };

  const handleSaveClient = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/${clientId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editData)
      });
      if (response.ok) {
        toast.success(language === 'es' ? '¡Cliente actualizado!' : 'Client updated!');
        setIsEditing(false);
        fetchClient();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.content.trim()) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newNote, client_id: clientId })
      });
      if (response.ok) {
        toast.success(language === 'es' ? '¡Nota agregada!' : 'Note added!');
        setNewNote({ content: '', note_type: 'general' });
        fetchClient();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await fetch(`${BACKEND_URL}/api/notes/${noteId}`, { method: 'DELETE' });
      toast.success(language === 'es' ? 'Nota eliminada' : 'Note deleted');
      fetchClient();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleUploadDocument = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file size (max 50MB)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(language === 'es' ? 'Archivo demasiado grande (máx 50MB)' : 'File too large (max 50MB)');
      e.target.value = '';
      return;
    }
    
    // Show modal to get title/description (don't start upload yet)
    setPendingFile(file);
    setFileTitle(file.name);
    setFileDescription('');
    setShowDocModal(true);
  };

  const submitDocUpload = async () => {
    if (!pendingFile) return;
    setUploadingDoc(true);
    setShowDocModal(false);
    
    const uploadToast = toast.loading(language === 'es' ? `Subiendo ${pendingFile.name}...` : `Uploading ${pendingFile.name}...`);
    
    try {
      const formData = new FormData();
      formData.append('file', pendingFile);
      formData.append('client_id', clientId);
      formData.append('title', fileTitle || pendingFile.name);
      formData.append('description', fileDescription);
      
      const response = await fetch(`${BACKEND_URL}/api/documents/upload`, {
        method: 'POST',
        body: formData
      });
      
      toast.dismiss(uploadToast);
      
      if (response.ok) {
        toast.success(language === 'es' ? `¡${pendingFile.name} subido!` : `${pendingFile.name} uploaded!`);
        fetchClient();
      } else {
        const errorText = await response.text();
        console.error('Upload error:', errorText);
        toast.error(language === 'es' ? `Error al subir ${pendingFile.name}` : `Failed to upload ${pendingFile.name}`);
      }
    } catch (error) {
      toast.dismiss(uploadToast);
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error de conexión - archivo muy grande?' : 'Connection error - file too large?');
    } finally {
      setUploadingDoc(false);
      setPendingFile(null);
      setFileTitle('');
      setFileDescription('');
      if (docInputRef.current) docInputRef.current.value = '';
    }
  };

  const handleUploadPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Show modal to get title/description
    setPendingFile(file);
    setFileTitle(file.name.replace(/\.[^/.]+$/, '')); // Remove extension for title
    setFileDescription('');
    setShowPhotoModal(true);
  };

  // Bulk photo upload handler
  const handleUploadPhotoBulk = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    if (files.length > 5) {
      toast.error(language === 'es' ? 'Máximo 5 fotos a la vez' : 'Maximum 5 photos at once');
      e.target.value = '';
      return;
    }
    
    setUploadingPhoto(true);
    
    try {
      const formData = new FormData();
      formData.append('client_id', clientId);
      formData.append('photo_type', 'general');
      files.forEach(file => {
        formData.append('files', file);
      });
      
      const response = await fetch(`${BACKEND_URL}/api/photos/upload-bulk`, {
        method: 'POST',
        body: formData
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(
          language === 'es' 
            ? `${result.uploaded_count} foto(s) subida(s)` 
            : `${result.uploaded_count} photo(s) uploaded`
        );
        if (result.errors && result.errors.length > 0) {
          toast.warning(result.errors.join(', '));
        }
        fetchClient();
      } else {
        toast.error(result.detail || 'Upload failed');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error al subir' : 'Upload failed');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const submitPhotoUpload = async () => {
    if (!pendingFile) return;
    setUploadingPhoto(true);
    setShowPhotoModal(false);
    
    try {
      const formData = new FormData();
      formData.append('file', pendingFile);
      formData.append('client_id', clientId);
      formData.append('title', fileTitle || pendingFile.name);
      formData.append('description', fileDescription);
      formData.append('photo_type', 'general');
      
      const response = await fetch(`${BACKEND_URL}/api/photos/upload`, {
        method: 'POST',
        body: formData
      });
      if (response.ok) {
        toast.success(language === 'es' ? '¡Foto subida!' : 'Photo uploaded!');
        fetchClient();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Upload failed');
    } finally {
      setUploadingPhoto(false);
      setPendingFile(null);
      setFileTitle('');
      setFileDescription('');
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      await fetch(`${BACKEND_URL}/api/documents/${docId}`, { method: 'DELETE' });
      toast.success(language === 'es' ? 'Documento eliminado' : 'Document deleted');
      fetchClient();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await fetch(`${BACKEND_URL}/api/photos/${photoId}`, { method: 'DELETE' });
      toast.success(language === 'es' ? 'Foto eliminada' : 'Photo deleted');
      fetchClient();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { color: 'bg-green-500', label: language === 'es' ? 'Activo' : 'Active' },
      in_progress: { color: 'bg-blue-500', label: language === 'es' ? 'En Progreso' : 'In Progress' },
      closed: { color: 'bg-purple-500', label: language === 'es' ? 'Cerrado' : 'Closed' },
      overdue: { color: 'bg-red-500', label: language === 'es' ? 'Vencido' : 'Overdue' },
      archived: { color: 'bg-gray-500', label: language === 'es' ? 'Archivado' : 'Archived' }
    };
    const c = config[status] || config.active;
    return <Badge className={`${c.color} text-white`}>{c.label}</Badge>;
  };

  const getEstimateStatusBadge = (status) => {
    const config = {
      draft: { color: 'bg-gray-500', icon: Clock },
      sent: { color: 'bg-blue-500', icon: Send },
      accepted: { color: 'bg-green-500', icon: CheckCircle2 },
    };
    const c = config[status] || config.draft;
    const Icon = c.icon;
    return <Badge className={`${c.color} text-gray-900 text-xs`}><Icon className="w-3 h-3 mr-1" />{status}</Badge>;
  };

  const formatDate = (date) => date ? new Date(date).toLocaleDateString() : 'N/A';
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Project stage helper functions
  const getProjectStageBadge = (stage) => {
    const config = {
      permit_approved: { color: 'bg-orange-500 text-white', label: language === 'es' ? 'Permiso Aprobado' : 'Permit Approved' },
      first_inspection: { color: 'bg-yellow-500 text-white', label: language === 'es' ? '1ra Inspección' : '1st Inspection' },
      second_inspection: { color: 'bg-lime-500 text-white', label: language === 'es' ? '2da Inspección' : '2nd Inspection' },
      final_inspection: { color: 'bg-green-500 text-white', label: language === 'es' ? '3ra y Final' : '3rd & Final' },
      payment_received: { color: 'bg-emerald-600 text-white', label: language === 'es' ? 'Pago Recibido' : 'Payment Received' }
    };
    return config[stage] || config.permit_approved;
  };

  const getProjectProgress = (stage) => {
    const progressMap = {
      permit_approved: 20,
      first_inspection: 40,
      second_inspection: 60,
      final_inspection: 80,
      payment_received: 100
    };
    return progressMap[stage] || 20;
  };

  // Construction stages array (for PDF and flow chart)
  const constructionStages = [
    { key: 'contract_signed', label: 'Contract Signed', label_es: 'Contrato Firmado', phase: 'pre' },
    { key: 'permit_applied', label: 'Permit Applied For', label_es: 'Permiso Solicitado', phase: 'pre' },
    { key: 'permit_granted', label: 'Permit Granted', label_es: 'Permiso Otorgado', phase: 'pre' },
    { key: 'hoa_filed', label: 'HOA Application Filed', label_es: 'Solicitud HOA Presentada', phase: 'pre' },
    { key: 'hoa_granted', label: 'HOA Application Granted', label_es: 'Solicitud HOA Aprobada', phase: 'pre' },
    { key: 'materials_ordered', label: 'Materials Ordered from Manufacturer', label_es: 'Materiales Ordenados del Fabricante', phase: 'materials' },
    { key: 'delivery_date_set', label: 'Materials Delivery Date Set', label_es: 'Fecha de Entrega Establecida', phase: 'materials' },
    { key: 'materials_delivered', label: 'Materials Delivered', label_es: 'Materiales Entregados', phase: 'materials' },
    { key: 'materials_confirmed', label: 'Materials Confirmed', label_es: 'Materiales Confirmados', phase: 'materials' },
    { key: 'change_orders_submitted', label: 'Change Orders Submitted', label_es: 'Órdenes de Cambio Enviadas', phase: 'materials' },
    { key: 'subcontractors_hired', label: 'Subcontractors Hired', label_es: 'Subcontratistas Contratados', phase: 'construction' },
    { key: 'demolition_begins', label: 'Demolition Begins', label_es: 'Demolición Comienza', phase: 'construction' },
    { key: 'rotted_wood_replaced', label: 'Rotted Wood Replaced', label_es: 'Madera Podrida Reemplazada', phase: 'construction' },
    { key: 'demolition_completed', label: 'Demolition Completed', label_es: 'Demolición Completada', phase: 'construction' },
    { key: 'underlayment_installed', label: 'Underlayment Installed', label_es: 'Membrana Instalada', phase: 'construction' },
    { key: 'water_shield_installed', label: 'Water Shield Installed', label_es: 'Protección de Agua Instalada', phase: 'construction' },
    { key: 'first_inspection_called', label: 'First Inspection Called In', label_es: 'Primera Inspección Solicitada', phase: 'inspection' },
    { key: 'first_inspection_passed', label: 'First Inspection Passed', label_es: 'Primera Inspección Aprobada', phase: 'inspection' },
    { key: 'second_payment_paid', label: 'Second Payment Invoiced and Paid', label_es: 'Segundo Pago Facturado y Pagado', phase: 'payment' },
    { key: 'first_quarter_installed', label: 'First Quarter Tile Installed', label_es: 'Primer Cuarto de Teja Instalado', phase: 'construction' },
    { key: 'progress_inspection', label: 'Progress Inspection Completed', label_es: 'Inspección de Progreso Completada', phase: 'inspection' },
    { key: 'second_third_installed', label: '2nd Third of Roof Installed', label_es: 'Segundo Tercio del Techo Instalado', phase: 'construction' },
    { key: 'wind_lift_test', label: 'Wind Lift Test Completed', label_es: 'Prueba de Elevación por Viento Completada', phase: 'inspection' },
    { key: 'final_inspection', label: 'Final Inspection Completed', label_es: 'Inspección Final Completada', phase: 'inspection' },
    { key: 'final_payment_invoiced', label: 'Final Payment Invoiced', label_es: 'Pago Final Facturado', phase: 'payment' },
    { key: 'final_payment_made', label: 'Final Payment Made', label_es: 'Pago Final Realizado', phase: 'payment' },
    { key: 'project_closed', label: 'Project Closed', label_es: 'Proyecto Cerrado', phase: 'closed' },
  ];

  // Generate Client Card PDF
  const generateClientPDF = async () => {
    setGeneratingPdf(true);
    try {
      // Create a temporary printable div
      const printContent = document.createElement('div');
      printContent.style.position = 'absolute';
      printContent.style.left = '-9999px';
      printContent.style.top = '0';
      printContent.style.width = '816px';
      printContent.style.padding = '36px';
      printContent.style.backgroundColor = '#ffffff';
      printContent.style.fontFamily = 'Arial, sans-serif';
      printContent.style.fontSize = '12px';
      printContent.style.zIndex = '-1';
      
      const completedStages = constructionStages.filter(s => client.construction_progress?.[s.key]);
      const progressPercent = Math.round((completedStages.length / constructionStages.length) * 100);
      
      printContent.innerHTML = `
        <div style="border-bottom: 3px solid ${COMPANY.primaryColor}; padding-bottom: 20px; margin-bottom: 20px;">
          <h1 style="margin: 0; font-size: 24px; color: ${COMPANY.primaryColor};">${COMPANY.name}</h1>
          <p style="margin: 5px 0 0; color: #666;">${language === 'es' ? 'Ficha del Cliente' : 'Client Card'} - ${new Date().toLocaleDateString()}</p>
        </div>
        
        <div style="margin-bottom: 20px; padding: 15px; background: #f9f9f9; border-radius: 8px;">
          <h2 style="margin: 0 0 10px; font-size: 18px; color: #333;">${client.name}</h2>
          <p style="margin: 3px 0; color: #555;"><strong>${language === 'es' ? 'Teléfono' : 'Phone'}:</strong> ${client.phone || 'N/A'}</p>
          <p style="margin: 3px 0; color: #555;"><strong>Email:</strong> ${client.email || 'N/A'}</p>
          <p style="margin: 3px 0; color: #555;"><strong>${language === 'es' ? 'Dirección' : 'Address'}:</strong> ${client.address || 'N/A'}</p>
          <p style="margin: 3px 0; color: #555;"><strong>${language === 'es' ? 'Categoría' : 'Category'}:</strong> ${client.project_category || 'N/A'}</p>
        </div>
        
        <div style="margin-bottom: 20px;">
          <h3 style="margin: 0 0 10px; font-size: 16px; border-bottom: 1px solid #ddd; padding-bottom: 5px;">${language === 'es' ? 'Progreso del Proyecto' : 'Project Progress'} - ${progressPercent}%</h3>
          <div style="background: #e5e5e5; border-radius: 8px; height: 20px; overflow: hidden; margin-bottom: 15px;">
            <div style="background: ${COMPANY.primaryColor}; height: 100%; width: ${progressPercent}%;"></div>
          </div>
          <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
            <thead>
              <tr style="background: #f3f3f3;">
                <th style="padding: 8px; text-align: left; border: 1px solid #ddd;">#</th>
                <th style="padding: 8px; text-align: left; border: 1px solid #ddd;">${language === 'es' ? 'Etapa' : 'Stage'}</th>
                <th style="padding: 8px; text-align: center; border: 1px solid #ddd;">${language === 'es' ? 'Estado' : 'Status'}</th>
                <th style="padding: 8px; text-align: center; border: 1px solid #ddd;">${language === 'es' ? 'Fecha' : 'Date'}</th>
              </tr>
            </thead>
            <tbody>
              ${constructionStages.map((stage, i) => {
                const isCompleted = client.construction_progress?.[stage.key];
                const completedDate = client.construction_dates?.[stage.key] || '';
                return `
                  <tr style="background: ${isCompleted ? '#e8f5e9' : '#fff'};">
                    <td style="padding: 6px 8px; border: 1px solid #ddd;">${i + 1}</td>
                    <td style="padding: 6px 8px; border: 1px solid #ddd;">${language === 'es' ? stage.label_es : stage.label}</td>
                    <td style="padding: 6px 8px; border: 1px solid #ddd; text-align: center;">${isCompleted ? '✓' : '○'}</td>
                    <td style="padding: 6px 8px; border: 1px solid #ddd; text-align: center;">${completedDate || '-'}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </div>
        
        ${(client.notes && client.notes.length > 0) ? `
          <div style="margin-bottom: 20px;">
            <h3 style="margin: 0 0 10px; font-size: 16px; border-bottom: 1px solid #ddd; padding-bottom: 5px;">${language === 'es' ? 'Notas' : 'Notes'} (${client.notes.length})</h3>
            ${client.notes.map(note => `
              <div style="background: #fffde7; border: 1px solid #fff59d; border-radius: 6px; padding: 10px; margin-bottom: 8px;">
                <p style="margin: 0 0 5px; color: #333;">${note.content}</p>
                <p style="margin: 0; font-size: 10px; color: #888;">${new Date(note.created_at).toLocaleDateString()}</p>
              </div>
            `).join('')}
          </div>
        ` : ''}
        
        ${(client.documents && client.documents.length > 0) ? `
          <div style="margin-bottom: 20px;">
            <h3 style="margin: 0 0 10px; font-size: 16px; border-bottom: 1px solid #ddd; padding-bottom: 5px;">${language === 'es' ? 'Documentos' : 'Documents'} (${client.documents.length})</h3>
            <ul style="margin: 0; padding-left: 20px;">
              ${client.documents.map(doc => `<li style="margin: 3px 0;">${doc.file_name || doc.name || 'Document'}</li>`).join('')}
            </ul>
          </div>
        ` : ''}
        
        ${(client.photos && client.photos.length > 0) ? `
          <div style="margin-bottom: 20px;">
            <h3 style="margin: 0 0 10px; font-size: 16px; border-bottom: 1px solid #ddd; padding-bottom: 5px;">${language === 'es' ? 'Fotos' : 'Photos'} (${client.photos.length})</h3>
            <div style="display: flex; flex-wrap: wrap; gap: 10px;">
              ${client.photos.slice(0, 6).map(photo => `
                <img src="${BACKEND_URL}/api/photos/${photo.id}/file" style="width: 120px; height: 90px; object-fit: cover; border-radius: 6px; border: 1px solid #ddd;" onerror="this.style.display='none'" />
              `).join('')}
              ${client.photos.length > 6 ? `<p style="color: #666;">+${client.photos.length - 6} ${language === 'es' ? 'más' : 'more'}</p>` : ''}
            </div>
          </div>
        ` : ''}
        
        <div style="border-top: 1px solid #ddd; padding-top: 15px; margin-top: 20px; text-align: center; color: #888; font-size: 10px;">
          <p style="margin: 0;">${COMPANY.name} | ${COMPANY.phone} | ${COMPANY.email}</p>
          <p style="margin: 5px 0 0;">${language === 'es' ? 'Generado el' : 'Generated on'} ${new Date().toLocaleString()}</p>
        </div>
      `;
      
      document.body.appendChild(printContent);
      
      // Wait for DOM to render
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const canvas = await html2canvas(printContent, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: true,
        allowTaint: true,
        windowWidth: 816,
        windowHeight: printContent.scrollHeight
      });
      
      document.body.removeChild(printContent);
      
      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        throw new Error('Canvas generation failed');
      }
      
      // US Letter size PDF
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
      const pdfWidth = 215.9;
      const pdfHeight = 279.4;
      const margin = 12.7;
      const contentWidth = pdfWidth - (margin * 2);
      const contentHeight = pdfHeight - (margin * 2) - 10;
      
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const totalPages = Math.ceil(imgHeight / contentHeight);
      const canvasHeightPerPage = canvas.height / totalPages;
      
      for (let page = 0; page < totalPages; page++) {
        if (page > 0) pdf.addPage();
        
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = Math.ceil(canvasHeightPerPage);
        const ctx = pageCanvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        ctx.drawImage(canvas, 0, page * canvasHeightPerPage, canvas.width, canvasHeightPerPage, 0, 0, canvas.width, canvasHeightPerPage);
        
        const pageImgHeight = Math.min((pageCanvas.height * imgWidth) / pageCanvas.width, contentHeight);
        pdf.addImage(pageCanvas.toDataURL('image/jpeg', 0.95), 'JPEG', margin, margin, imgWidth, pageImgHeight);
        
        pdf.setFontSize(9);
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Page ${page + 1} of ${totalPages}`, pdfWidth / 2, pdfHeight - 5, { align: 'center' });
      }
      
      const clientName = client.name ? client.name.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_') : 'Client';
      const fileName = `ClientCard_${clientName}_${new Date().toISOString().split('T')[0]}.pdf`;
      
      pdf.save(fileName);
      toast.success(language === 'es' ? `PDF guardado: ${fileName}` : `PDF saved: ${fileName}`);
    } catch (error) {
      console.error('PDF Generation Error:', error);
      toast.error(language === 'es' ? 'Error al generar PDF' : 'Failed to generate PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const tabs = [
    { id: 'overview', label: language === 'es' ? 'Resumen' : 'Overview', icon: Users },
    { id: 'project', label: language === 'es' ? 'Proyecto' : 'Project', icon: ClipboardList },
    { id: 'accounting', label: language === 'es' ? 'Contabilidad' : 'Accounting', icon: DollarSign },
    { id: 'contacts', label: language === 'es' ? 'Contactos' : 'Contacts', icon: UserPlus, count: contacts.length },
    { id: 'proposals', label: language === 'es' ? 'Propuestas' : 'Proposals', icon: FileText, count: clientEstimates?.length || 0 },
    { id: 'notes', label: language === 'es' ? 'Notas' : 'Notes', icon: StickyNote, count: client?.notes?.length },
    { id: 'documents', label: language === 'es' ? 'Documentos' : 'Documents', icon: FolderOpen, count: client?.documents?.length },
    { id: 'photos', label: language === 'es' ? 'Fotos' : 'Photos', icon: Image, count: client?.photos?.length },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!client) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-amber-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <nav className="container mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/clients">
                <Button variant="ghost" className="text-gray-600 hover:text-gray-900">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {language === 'es' ? 'Clientes' : 'Clients'}
                </Button>
              </Link>
            </div>
            <div className="flex items-center space-x-2">
              <LanguageToggle />
              <Button onClick={createNewProposal} style={{ backgroundColor: COMPANY.primaryColor }} className="text-gray-900">
                <Plus className="mr-2 h-4 w-4" />
                {language === 'es' ? 'Nueva Propuesta' : 'New Proposal'}
              </Button>
            </div>
          </div>
        </nav>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-6">
        {/* Client Header */}
        <Card className="bg-white border-gray-200 mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
              <div className="flex items-start space-x-4">
                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
                  <Users className="h-8 w-8 text-amber-500" />
                </div>
                <div>
                  <span className="text-amber-500 font-mono text-sm">{client.client_number}</span>
                  <h1 className="text-2xl font-bold text-gray-900">{client.name}</h1>
                  {client.company && <p className="text-gray-600">{client.company}</p>}
                  <div className="mt-2">{getStatusBadge(client.status)}</div>
                </div>
              </div>
              <div className="flex space-x-2">
                {isEditing ? (
                  <>
                    <Button variant="outline" onClick={() => setIsEditing(false)} className="border-gray-300 text-gray-600">
                      <X className="mr-2 h-4 w-4" />{language === 'es' ? 'Cancelar' : 'Cancel'}
                    </Button>
                    <Button onClick={handleSaveClient} className="bg-green-600 text-gray-900">
                      <Save className="mr-2 h-4 w-4" />{language === 'es' ? 'Guardar' : 'Save'}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button 
                      variant="outline" 
                      onClick={generateClientPDF} 
                      disabled={generatingPdf}
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      data-testid="print-client-card-btn"
                    >
                      <Printer className="mr-2 h-4 w-4" />
                      {generatingPdf 
                        ? (language === 'es' ? 'Generando...' : 'Generating...') 
                        : (language === 'es' ? 'Imprimir PDF' : 'Print PDF')}
                    </Button>
                    <Button variant="outline" onClick={() => setIsEditing(true)} className="border-gray-300 text-gray-600">
                      <Edit className="mr-2 h-4 w-4" />{language === 'es' ? 'Editar' : 'Edit'}
                    </Button>
                  </>
                )}
              </div>
            </div>
            
            {/* Contact Info */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center text-gray-600">
                <Mail className="h-5 w-5 mr-3 text-gray-500" />
                {isEditing ? (
                  <Input value={editData.email || ''} onChange={(e) => setEditData({...editData, email: e.target.value})} className="bg-gray-100 border-gray-300 text-gray-900 h-8" />
                ) : (
                  <span>{client.email || 'No email'}</span>
                )}
              </div>
              <div className="flex items-center text-gray-600">
                <Phone className="h-5 w-5 mr-3 text-gray-500" />
                {isEditing ? (
                  <Input value={editData.phone || ''} onChange={(e) => setEditData({...editData, phone: e.target.value})} className="bg-gray-100 border-gray-300 text-gray-900 h-8" />
                ) : (
                  <span>{client.phone || 'No phone'}</span>
                )}
              </div>
              <div className="flex items-center text-gray-600">
                <MapPin className="h-5 w-5 mr-3 text-gray-500" />
                {isEditing ? (
                  <Input value={editData.address || ''} onChange={(e) => setEditData({...editData, address: e.target.value})} className="bg-gray-100 border-gray-300 text-gray-900 h-8" />
                ) : (
                  <span>{client.address || 'No address'}</span>
                )}
              </div>
            </div>
            
            {isEditing && (
              <div className="mt-4">
                <label className="text-gray-600 text-sm">{language === 'es' ? 'Estado' : 'Status'}</label>
                <Select value={editData.status} onValueChange={(v) => setEditData({...editData, status: v})}>
                  <SelectTrigger className="bg-gray-100 border-gray-300 text-gray-900 w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{language === 'es' ? 'Activo' : 'Active'}</SelectItem>
                    <SelectItem value="in_progress">{language === 'es' ? 'En Progreso' : 'In Progress'}</SelectItem>
                    <SelectItem value="closed">{language === 'es' ? 'Cerrado' : 'Closed'}</SelectItem>
                    <SelectItem value="overdue">{language === 'es' ? 'Vencido' : 'Overdue'}</SelectItem>
                    <SelectItem value="archived">{language === 'es' ? 'Archivado' : 'Archived'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex space-x-1 mb-6 overflow-x-auto pb-2">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? 'default' : 'ghost'}
                onClick={() => setActiveTab(tab.id)}
                className={activeTab === tab.id ? 'bg-amber-500 text-gray-900' : 'text-gray-600 hover:text-gray-900'}
              >
                <Icon className="mr-2 h-4 w-4" />
                {tab.label}
                {tab.count > 0 && (
                  <span className="ml-2 bg-gray-100 text-gray-300 rounded-full px-2 py-0.5 text-xs">{tab.count}</span>
                )}
              </Button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <ClientOverviewTab
            language={language}
            client={client}
            clientEstimates={clientEstimates}
            contacts={contacts}
            accountingSummary={accountingSummary}
            accountingInvoices={accountingInvoices}
            // Edit State
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            editData={editData}
            setEditData={setEditData}
            // Handlers
            handleInputChange={handleInputChange}
            handleSaveClient={async () => {
              try {
                const response = await fetch(`${BACKEND_URL}/api/clients/${clientId}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ ...client, ...editData })
                });
                if (response.ok) {
                  setClient({ ...client, ...editData });
                  setIsEditing(false);
                  toast.success(language === 'es' ? '¡Cliente actualizado!' : 'Client updated!');
                }
              } catch (error) {
                toast.error(language === 'es' ? 'Error al guardar' : 'Error saving');
              }
            }}
            setActiveTab={setActiveTab}
            // Utilities
            formatDate={formatDate}
            formatCurrency={formatCurrency}
          />
        )}


        {/* PROJECT TAB - Construction Flow Chart */}
        {activeTab === 'project' && (
          <ClientProjectTab
            language={language}
            client={client}
            setClient={setClient}
            clientId={clientId}
            clientEstimates={clientEstimates}
            newProjectNote={newProjectNote}
            setNewProjectNote={setNewProjectNote}
          />
        )}


        {/* ACCOUNTING TAB */}
        {activeTab === 'accounting' && (
          <ClientAccountingTab
            language={language}
            client={client}
            // Summary Data
            accountingSummary={accountingSummary}
            accountingPayments={accountingPayments}
            accountingCosts={accountingCosts}
            accountingInvoices={accountingInvoices}
            accountingActivities={accountingActivities}
            costCategories={costCategories}
            clientEstimates={clientEstimates}
            changeOrders={changeOrders}
            // Contract Amount Edit
            editingContractAmount={editingContractAmount}
            setEditingContractAmount={setEditingContractAmount}
            contractAmountOverride={contractAmountOverride}
            setContractAmountOverride={setContractAmountOverride}
            handleSaveContractAmount={handleSaveContractAmount}
            // Invoice State & Handlers
            showCreateInvoice={showCreateInvoice}
            setShowCreateInvoice={setShowCreateInvoice}
            newInvoice={newInvoice}
            setNewInvoice={setNewInvoice}
            editingInvoice={editingInvoice}
            setEditingInvoice={setEditingInvoice}
            handleCreateInvoice={handleCreateInvoice}
            handleSendInvoice={handleSendInvoice}
            handleDeleteInvoice={handleDeleteInvoice}
            handleEditInvoice={handleEditInvoice}
            generateInvoicePDF={generateInvoicePDF}
            // Change Order State & Handlers
            showCreateChangeOrder={showCreateChangeOrder}
            setShowCreateChangeOrder={setShowCreateChangeOrder}
            newChangeOrder={newChangeOrder}
            setNewChangeOrder={setNewChangeOrder}
            editingChangeOrder={editingChangeOrder}
            setEditingChangeOrder={setEditingChangeOrder}
            handleCreateChangeOrder={handleCreateChangeOrder}
            handleUpdateChangeOrder={handleUpdateChangeOrder}
            handleDeleteChangeOrder={handleDeleteChangeOrder}
            handleEditChangeOrderSave={handleEditChangeOrderSave}
            addChangeOrderLineItem={addChangeOrderLineItem}
            updateChangeOrderLineItem={updateChangeOrderLineItem}
            removeChangeOrderLineItem={removeChangeOrderLineItem}
            addEditingChangeOrderLineItem={addEditingChangeOrderLineItem}
            updateEditingChangeOrderLineItem={updateEditingChangeOrderLineItem}
            removeEditingChangeOrderLineItem={removeEditingChangeOrderLineItem}
            calculateChangeOrderTotal={calculateChangeOrderTotal}
            generateChangeOrderPDF={generateChangeOrderPDF}
            // Payment State & Handlers
            showAddPayment={showAddPayment}
            setShowAddPayment={setShowAddPayment}
            newPayment={newPayment}
            setNewPayment={setNewPayment}
            handleAddPayment={handleAddPayment}
            handleDeletePayment={handleDeletePayment}
            // Cost State & Handlers
            showAddCost={showAddCost}
            setShowAddCost={setShowAddCost}
            newCost={newCost}
            setNewCost={setNewCost}
            editingCost={editingCost}
            setEditingCost={setEditingCost}
            handleAddCost={handleAddCost}
            handleDeleteCost={handleDeleteCost}
            handleEditCost={handleEditCost}
            // Vendor State & Handlers
            vendorSearch={vendorSearch}
            handleVendorSearch={handleVendorSearch}
            vendorSearchResults={vendorSearchResults}
            showVendorDropdown={showVendorDropdown}
            setShowVendorDropdown={setShowVendorDropdown}
            selectVendor={selectVendor}
            showCreateVendor={showCreateVendor}
            setShowCreateVendor={setShowCreateVendor}
            newVendorForm={newVendorForm}
            setNewVendorForm={setNewVendorForm}
            createVendorContact={createVendorContact}
            // Export
            handleExportReport={handleExportReport}
            // Utility
            formatCurrency={formatCurrency}
          />
        )}


        {activeTab === 'proposals' && (
          <ClientProposalsTab
            language={language}
            clientEstimates={clientEstimates}
            createNewProposal={createNewProposal}
            formatDate={formatDate}
            formatCurrency={formatCurrency}
          />
        )}

        {/* CONTACTS TAB */}
        {activeTab === 'contacts' && (
          <ClientContactsTab
            language={language}
            contacts={contacts}
            openNewContactModal={openNewContactModal}
            openEditContactModal={openEditContactModal}
            deleteContact={deleteContact}
          />
        )}

                {activeTab === 'notes' && (
          <ClientNotesTab
            language={language}
            client={client}
            newNote={newNote}
            setNewNote={setNewNote}
            handleAddNote={handleAddNote}
            handleDeleteNote={handleDeleteNote}
            formatDate={formatDate}
          />
        )}

                {activeTab === 'documents' && (
          <ClientDocumentsTab
            language={language}
            client={client}
            docInputRef={docInputRef}
            uploadingDoc={uploadingDoc}
            handleUploadDocument={handleUploadDocument}
            handleDeleteDocument={handleDeleteDocument}
            formatDate={formatDate}
          />
        )}

                {activeTab === 'photos' && (
          <ClientPhotosTab
            language={language}
            client={client}
            photoInputRef={photoInputRef}
            uploadingPhoto={uploadingPhoto}
            handleUploadPhotoBulk={handleUploadPhotoBulk}
            handleDeletePhoto={handleDeletePhoto}
          />
        )}
      </main>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle>
                  {editingContact 
                    ? (language === 'es' ? 'Editar Contacto' : 'Edit Contact')
                    : (language === 'es' ? 'Agregar Contacto' : 'Add Contact')
                  }
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={closeContactModal}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>{language === 'es' ? 'Nombre' : 'Name'} *</Label>
                <Input
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  placeholder={language === 'es' ? 'Nombre completo' : 'Full name'}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>{language === 'es' ? 'Rol' : 'Role'}</Label>
                <Select value={contactForm.role} onValueChange={(v) => setContactForm({ ...contactForm, role: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CONTACT_ROLES).map(([key, role]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: role.color }} />
                          {language === 'es' ? role.labelEs : role.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>{language === 'es' ? 'Compañía' : 'Company'}</Label>
                <Input
                  value={contactForm.company}
                  onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                  className="mt-1"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{language === 'es' ? 'Teléfono' : 'Phone'}</Label>
                  <Input
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>{language === 'es' ? 'Móvil' : 'Mobile'}</Label>
                  <Input
                    value={contactForm.mobile}
                    onChange={(e) => setContactForm({ ...contactForm, mobile: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>{language === 'es' ? 'Notas' : 'Notes'}</Label>
                <Textarea
                  value={contactForm.notes}
                  onChange={(e) => setContactForm({ ...contactForm, notes: e.target.value })}
                  className="mt-1"
                  rows={2}
                />
              </div>
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={closeContactModal}>
                  {language === 'es' ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button 
                  onClick={saveContact}
                  disabled={!contactForm.name.trim()}
                  style={{ backgroundColor: COMPANY.primaryColor }}
                  className="text-white"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {language === 'es' ? 'Guardar' : 'Save'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Photo Upload Modal */}
      {showPhotoModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle>{language === 'es' ? 'Subir Foto' : 'Upload Photo'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => { setShowPhotoModal(false); setPendingFile(null); setFileTitle(''); setFileDescription(''); }}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {pendingFile && (
                <div className="flex items-center gap-3 p-3 bg-gray-100 rounded-lg">
                  <Image className="h-10 w-10 text-amber-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{pendingFile.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(pendingFile.size)}</p>
                  </div>
                </div>
              )}
              <div>
                <Label>{language === 'es' ? 'Título' : 'Title'}</Label>
                <Input
                  value={fileTitle}
                  onChange={(e) => setFileTitle(e.target.value)}
                  placeholder={language === 'es' ? 'Título de la foto' : 'Photo title'}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>{language === 'es' ? 'Descripción' : 'Description'}</Label>
                <Textarea
                  value={fileDescription}
                  onChange={(e) => setFileDescription(e.target.value)}
                  placeholder={language === 'es' ? 'Agregar descripción o notas...' : 'Add description or notes...'}
                  className="mt-1"
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => { setShowPhotoModal(false); setPendingFile(null); setFileTitle(''); setFileDescription(''); }}>
                  {language === 'es' ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button 
                  onClick={submitPhotoUpload}
                  disabled={uploadingPhoto}
                  style={{ backgroundColor: COMPANY.primaryColor }}
                  className="text-white"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploadingPhoto ? '...' : (language === 'es' ? 'Subir' : 'Upload')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Document Upload Modal */}
      {showDocModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle>{language === 'es' ? 'Subir Documento' : 'Upload Document'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => { setShowDocModal(false); setPendingFile(null); setFileTitle(''); setFileDescription(''); }}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {pendingFile && (
                <div className="flex items-center gap-3 p-3 bg-gray-100 rounded-lg">
                  <FolderOpen className="h-10 w-10 text-amber-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{pendingFile.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(pendingFile.size)}</p>
                  </div>
                </div>
              )}
              <div>
                <Label>{language === 'es' ? 'Título' : 'Title'}</Label>
                <Input
                  value={fileTitle}
                  onChange={(e) => setFileTitle(e.target.value)}
                  placeholder={language === 'es' ? 'Título del documento' : 'Document title'}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>{language === 'es' ? 'Descripción' : 'Description'}</Label>
                <Textarea
                  value={fileDescription}
                  onChange={(e) => setFileDescription(e.target.value)}
                  placeholder={language === 'es' ? 'Agregar descripción o notas...' : 'Add description or notes...'}
                  className="mt-1"
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => { setShowDocModal(false); setPendingFile(null); setFileTitle(''); setFileDescription(''); }}>
                  {language === 'es' ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button 
                  onClick={submitDocUpload}
                  disabled={uploadingDoc}
                  style={{ backgroundColor: COMPANY.primaryColor }}
                  className="text-white"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploadingDoc ? '...' : (language === 'es' ? 'Subir' : 'Upload')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ClientDetail;
